import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  ClipboardList,
  CreditCard,
  Inbox,
  ArrowRight,
  Loader2,
  Download,
} from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "./AdminLayout";
import { Card, fmtDateTime } from "./_shared";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, l] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/logs", { params: { limit: 8 } }),
        ]);
        if (!cancelled) {
          setStats(s.data);
          setLogs(l.data.items || []);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const exportLinks = [
    { label: "Users", href: `${BACKEND_URL}/api/admin/export/users.csv`, testid: "export-users" },
    {
      label: "Assessments",
      href: `${BACKEND_URL}/api/admin/export/assessments.csv`,
      testid: "export-assessments",
    },
    {
      label: "Subscriptions",
      href: `${BACKEND_URL}/api/admin/export/subscriptions.csv`,
      testid: "export-subscriptions",
    },
    {
      label: "Contact",
      href: `${BACKEND_URL}/api/admin/export/contact.csv`,
      testid: "export-contact",
    },
  ];

  return (
    <AdminLayout
      title="Overview"
      subtitle="A snapshot of platform health and the most recent admin activity."
    >
      {loading ? (
        <div
          className="flex items-center gap-2 text-slate-500 text-sm"
          data-testid="admin-dashboard-loading"
        >
          <Loader2 className="h-4 w-4 animate-spin" /> Loading...
        </div>
      ) : (
        <div className="space-y-8" data-testid="admin-dashboard-content">
          <section
            className="grid grid-cols-2 lg:grid-cols-4 gap-4"
            data-testid="admin-stats-grid"
          >
            <StatCard
              icon={Users}
              label="Users"
              primary={stats?.users?.total ?? 0}
              secondary={`${stats?.users?.admins ?? 0} admin`}
              to="/admin/users"
              testid="stat-users"
            />
            <StatCard
              icon={ClipboardList}
              label="Assessments"
              primary={stats?.assessments?.total ?? 0}
              secondary={`${stats?.assessments?.processed ?? 0} with report`}
              to="/admin/assessments"
              testid="stat-assessments"
            />
            <StatCard
              icon={CreditCard}
              label="Subscriptions"
              primary={stats?.subscriptions?.active ?? 0}
              secondary={`of ${stats?.subscriptions?.total ?? 0} total`}
              to="/admin/subscriptions"
              testid="stat-subscriptions"
            />
            <StatCard
              icon={Inbox}
              label="Contact"
              primary={stats?.contact?.new ?? 0}
              secondary={`new of ${stats?.contact?.total ?? 0}`}
              to="/admin/contact"
              testid="stat-contact"
            />
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2 p-5" testid="admin-recent-logs-card">
              <header className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-medium text-slate-900">Recent admin activity</h2>
                <Link
                  to="/admin/logs"
                  className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                  data-testid="admin-recent-logs-view-all"
                >
                  View all <ArrowRight className="h-3 w-3" />
                </Link>
              </header>
              {logs.length === 0 ? (
                <div className="text-sm text-slate-500" data-testid="admin-recent-logs-empty">
                  No admin actions logged yet.
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {logs.map((l) => (
                    <li
                      key={l.log_id}
                      className="py-2.5 flex items-start justify-between gap-3 text-sm"
                      data-testid={`admin-log-row-${l.log_id}`}
                    >
                      <div className="min-w-0">
                        <div className="font-mono text-[11px] text-slate-500">{l.action}</div>
                        <div className="text-slate-700 truncate">
                          {l.actor_email}
                          {l.target_user_id ? ` → ${l.target_user_id}` : ""}
                        </div>
                      </div>
                      <div className="text-xs text-slate-400 shrink-0">{fmtDateTime(l.created_at)}</div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card className="p-5" testid="admin-export-card">
              <h2 className="text-sm font-medium text-slate-900 mb-3">Data export</h2>
              <p className="text-xs text-slate-500 mb-4">
                Download CSV snapshots of any data slice. Each export is logged.
              </p>
              <div className="space-y-2">
                {exportLinks.map((e) => (
                  <a
                    key={e.href}
                    href={e.href}
                    className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                    data-testid={`admin-${e.testid}-button`}
                  >
                    <span>{e.label}</span>
                    <Download className="h-3.5 w-3.5 text-slate-400" />
                  </a>
                ))}
              </div>
            </Card>
          </section>
        </div>
      )}
    </AdminLayout>
  );
}

function StatCard({ icon: Icon, label, primary, secondary, to, testid }) {
  return (
    <Link
      to={to}
      className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-slate-300 hover:-translate-y-0.5 transition-all"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.12em] text-slate-500">{label}</div>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div className="mt-3 text-3xl font-medium text-slate-900 tracking-tight">{primary}</div>
      <div className="mt-1 text-xs text-slate-500">{secondary}</div>
    </Link>
  );
}
